import React, { Component } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { I18n } from 'react-redux-i18n';
import { Tooltip } from 'antd';

import Button from 'components/Button';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Cell from 'components/Cell';
import ExportButton from 'components/Export';
import Table from 'components/Table';
import Header from 'components/Table/Header';
import Spinner from 'components/Spinner';
import ShowMoreButton from 'components/ShowMoreButton';

const isMobile = window.innerWidth < 845;

const AllNotationTabContainer = styled.div`
  position: relative;
  display: grid;

  .columnTextOverflow {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-left: 12px;
    padding-right: 12px;
  }

  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .hidden {
    display: none;
  }

  .rightComponentsContainer {
    display: flex;
    flex-direction: row;
    align-items: center;

    > div:not(:last-child) {
      margin-right: 10px;
    }
  }

  .table-notes {
    max-width: calc(100vw - 180px);

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      max-width: calc(100vw - 72px);
    }
  }

  .showAllContainer {
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;
    width: 200px;

    @media (max-width: 845px) {
      margin-top: 16px;
      width: 100%;
    }
  }
`;

export default class AllNotationTab extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hoverExport: false,
      sorted: [
        {
          id: 'date',
          desc: true
        }
      ],
      showAll: false
    };
  }

  handleSort(id) {
    let { sorted } = this.state;
    let desc = true;
    if (sorted.length > 0 && sorted[0].id === id) {
      desc = !sorted[0].desc;
    }
    sorted = [{ id, desc }];
    this.setState({ sorted });
  }

  getColumns() {
    return [
      {
        accessor: 'date',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.date')}
            sortable={true}
            sort={() => this.handleSort('date')}
          />
        ),
        Cell: (props) => (
          <Cell {...props} value={moment(props.value).format('DD/MM/YYYY')} />
        ),
        sortMethod: (a, b) => {
          return new Date(a) - new Date(b);
        },
        width: 120
      },
      {
        accessor: 'wine_name',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.wine')}
            sortable={true}
            sort={() => this.handleSort('wine_name')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="bottomLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        ),
        width: 120
      },
      {
        accessor: 'vintage',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.vintage')}
            sortable={true}
            sort={() => this.handleSort('vintage')}
          />
        ),
        Cell: (props) => <Cell {...props} />,
        width: 100
      },
      {
        accessor: 'journal_name',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.journal')}
            sortable={true}
            sort={() => this.handleSort('journal_name')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="bottomLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'degustateur_name',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.journalist')}
            sortable={true}
            sort={() => this.handleSort('degustateur_name')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="bottomLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'note_filtered',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.note')}
            sortable={true}
            sort={() => this.handleSort('note_filtered')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell font="sfcompact_text" {...props} styles={`font-weight: 600;`}>
            {props.value}
          </Cell>
        ),
        sortMethod: (a, b) => {
          if (!a) {
            a = '0';
          }
          if (!b) {
            b = '0';
          }
          if (isNaN(Number(a))) {
            if (a.includes('-')) {
              a = a.split('-')[1];
            }
          }
          if (isNaN(Number(b))) {
            if (b.includes('-')) {
              b = b.split('-')[1];
            }
          }
          a = a.replace(',', '.');
          b = b.replace(',', '.');

          a = a.replace('+', '');
          b = b.replace('+', '');
          return Number(a) - Number(b);
        },
        width: 85
      },
      this.props.hasWxrPanel && {
        accessor: 'note_type',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.type')}
            sortable={true}
            sort={() => this.handleSort('note_type')}
          />
        ),
        Cell: (props) => <Cell {...props} value={props.value} />,
        width: 100
      },
      {
        accessor: 'comment_filtered',
        Header: () => (
          <Header
            title={I18n.t('notes.allNotes.columns.comment')}
            sortable={true}
            sort={() => this.handleSort('comment_filtered')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => {
          const newProps = { ...props };
          newProps.value = newProps.original.login
            ? newProps.value
            : I18n.t('notes.allNotes.columns.sub_required');

          return (
            <Cell
              {...newProps}
              type={
                props.value &&
                props.value.length > 40 &&
                props.original.login &&
                'bigText'
              }
              styles={
                (props.value &&
                  props.value.length < 40 &&
                  `justify-content:flex-start;padding-left:30px;`,
                !props.original.login &&
                  `justify-content:flex-start;margin-left:10px;`)
              }
            />
          );
        },
        minWidth: isMobile ? 600 : 250
      }
    ];
  }

  sortDataByOrderDisplay() {
    const data = this.props.data || [];
    return data.sort(function (a, b) {
      return Number(a.order_display) - Number(b.order_display);
    });
  }

  renderButtonShowMore() {
    const data = this.sortDataByOrderDisplay();
    if (data.length <= 10) {
      return null;
    }

    const { showAll } = this.state;
    if (showAll) {
      // show less
      return (
        <ShowMoreButton
          label={`${I18n.t('travel.seeLess')} (10)`}
          onClick={() => this.setState({ showAll: false })}
        />
      );
    }

    // show more
    return (
      <ShowMoreButton
        label={`${I18n.t('travel.seeMoreTable')} (+${data.length - 10})`}
        onClick={() => this.setState({ showAll: true })}
      />
    );
  }

  render() {
    const { wine, vintage, journal, degustateur, type, exportName } =
      this.props;
    const { sorted, showAll } = this.state;
    let data = this.sortDataByOrderDisplay();
    if (!showAll) data = data.slice(0, 10);
    const columns = this.getColumns();

    return (
      <AllNotationTabContainer>
        <Card>
          <CardHeader
            title={I18n.t('notes.allNotes.tableTitle')}
            fontSize="20px"
            iconColor="#e25d34"
            iconRight
            rightComponents={
              <div className="rightComponentsContainer">
                <Button
                  type="round"
                  icon="plus"
                  iconColor="white"
                  className="hidden"
                />
                <div
                  className="exportButtonContainer"
                  onMouseEnter={() => this.setState({ hoverExport: true })}
                  onMouseLeave={() => this.setState({ hoverExport: false })}
                >
                  <ExportButton
                    name={exportName}
                    path="/communication/notation/raw/xls?"
                    options={{
                      wine,
                      vintage,
                      journal,
                      degustateur,
                      type
                    }}
                    xls
                    newApi
                  />
                </div>
              </div>
            }
          />
          <div className="table-notes">
            <Table
              data={data}
              columns={columns}
              sorted={sorted}
              subRowsKey={null}
            />
            <div className="showAllContainer ignored-export">
              {this.renderButtonShowMore()}
            </div>
          </div>
        </Card>
        <Spinner loading={this.props.loading} little />
      </AllNotationTabContainer>
    );
  }
}
